/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Theme } from '@mui/material'

export const navbarWrapperStyles = (theme: Theme) => css`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 10;
  height: 70px;
  padding: 0;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  border-bottom: 1px solid #d6d6d6;
  backdrop-filter: saturate(180%) blur(6px);
  background-color: ${theme.palette.neutral.main};
  ${theme.breakpoints.down('md')} {
    display: none;
  }
`

export const navbarWrapper = (theme: Theme) => css`
  display: none;
  position: sticky;
  top: 0;
  z-index: 10;
  height: 70px;
  padding: 0 20px 0 24px;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  border-bottom: 1px solid #d6d6d6;
  background-color: inherit;
  backdrop-filter: saturate(180%) blur(6px);

  ${theme.breakpoints.up('md')} {
    display: flex;
  }
`

export const NavbarTablist = css`
  display: flex;
  align-items: center;
  column-gap: 32px;
  align-self: stretch;
`

export const navbarListStyles = css`
  display: flex;
  align-items: center;
  column-gap: 32px;
  align-self: stretch;
`
export const navbarLogoStyles = (theme: Theme) => css`
  display: flex;
  max-width: 160px;
  align-items: center;
  justify-content: flex-end;
  color: white;
  margin-left: ${theme.spacing(10)};
  margin-right: ${theme.spacing(10)};
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
  }

  &:focus {
    outline-offset: 2px;
  }

  img {
    max-height: 56px;
    max-width: 100%;
    width: auto;
    object-fit: contain;
    display: block;
  }

  ${theme.breakpoints.down('md')} {
    width: 140px;

    img {
      max-height: 48px;
    }
  }

  ${theme.breakpoints.down('sm')} {
    width: 120px;

    img {
      max-height: 40px;
    }
  }
`
export const navbarItemStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`

export const navbarTriggerStyles = (theme: Theme, isActive?: boolean) => css`
  color: ${theme.palette.text.primary};
  text-decoration: none;
  text-transform: uppercase;
  font-weight: ${isActive ? 600 : 500};
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;

  &:hover {
    background-color: ${theme.palette.action.hover};
  }

  &:focus {
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: 2px;
  }

  ${isActive &&
  css`
    color: ${theme.palette.primary.main};
    background-color: ${theme.palette.action.selected};
  `}
`

export const navbarLinkStyles = (theme: Theme, isActive?: boolean) => css`
  color: ${theme.palette.text.primary};
  text-decoration: none;
  font-weight: ${isActive ? 600 : 500};
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: ${theme.palette.action.hover};
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: 2px;
  }

  ${isActive &&
  css`
    color: ${theme.palette.primary.main};
    background-color: ${theme.palette.action.selected};

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background-color: ${theme.palette.primary.main};
      border-radius: 1px;
    }
  `}
`

export const navbarContentStyles = (theme: Theme) => css`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.palette.background.paper};
  border: 1px solid ${theme.palette.divider};
  border-radius: 8px;
  box-shadow: ${theme.shadows[8]};
  z-index: 50;
  min-width: 200px;
  padding: 8px 0;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;

  &[data-state='open'] {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(4px);
  }
`

export const navbarSubStyles = css`
  display: flex;
  flex-direction: column;
`

export const navbarViewportStyles = css`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto;
`

export const navbarIndicatorStyles = (theme: Theme) => css`
  position: absolute;
  bottom: 0;
  height: 2px;
  background-color: ${theme.palette.primary.main};
  transition: all 0.3s ease;
  transform-origin: left;
`
const activeColor = '#0b2948'

export const linkStyle = css`
  font-family: Inter;
  position: relative;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  justify-content: center;
  padding: 0;
  min-height: 0;
  height: 100%;
  text-decoration: none;
  letter-spacing: 0.05rem;
  color: rgb(46, 101, 158);
  line-height: 1.25;
  text-transform: uppercase;
  transition: color 160ms ease;

  &:hover {
    text-decoration: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: ${activeColor};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 160ms ease;
  }

  &.active {
    color: ${activeColor};
  }
  &.active::after {
    transform: scaleX(1);
  }
`

export const innerPadding = css`
  padding: 18px 0;
`
export const root = (visible: boolean, theme: Theme) => css`
  display: inline-block;
  visibility: ${visible ? 'visible' : 'hidden'};

  ${theme?.breakpoints.down('md')} {
    display: none;
  }
`

export const fieldWrap = css`
  display: flex;
  align-items: center;
  width: 320px;
  height: 40px;
  border: 1px solid #d7dbe0;
  border-radius: 8px;
  background: #ffffff;
  padding: 2px;
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease;

  &:focus-within {
    border-color: #f6f6f6;
    box-shadow: 0 0 0 2px rgba(239, 239, 239, 0.35);
  }

  &[data-disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const inputCss = css`
  flex: 1 1 auto;
  height: 100%;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0 12px;
  font-size: 14px;
  line-height: 1;
  color: #0b2948;
  background: transparent;

  &::placeholder {
    color: #9aa7b8;
  }
`

export const buttonCss = css`
  flex: 0 0 auto;
  height: 100%;
  padding: 0 12px;
  border: none;
  outline: none;
  border-radius: 0;
  background: #2897ff;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 120ms ease,
    transform 50ms ease;

  &:hover {
    background: #1f88ea;
  }
  &:active {
    transform: translateY(0.5px);
  }
  &:disabled {
    cursor: not-allowed;
    background: #9ccbf9;
  }
`
